<script>
import { exportPaidArchive, getPaidArchive, getPaidDocuments } from '@/api/provisions'
import PriceColumn from '@/components/ui/datatable/columns/PriceColumn.vue'
import ActionButtonColumn from '@/components/ui/datatable/columns/ActionButtonColumn.vue'
import FormattingHelpers from '@/mixins/FormattingHelpers'
import UltimateSelectInput from '@/components/ui/inputs/UltimateSelectInput.vue'
import FeatureInput from '@/components/cars/edit/FeatureInput.vue'
import { DateTime } from 'luxon'
import Spinner from '@/components/ui/loader/Spinner.vue'
import download from 'downloadjs'
import Notice from '@/components/cars/edit/Notice.vue'

export default {
  components: {
    'mia-feature-input': FeatureInput,
    'mia-notice': Notice,
    'mia-spinner': Spinner,
    'mia-ultimate-select-input': UltimateSelectInput
  },

  mixins: [FormattingHelpers],

  data() {
    return {
      paidArchive: [],
      paidDocuments: [],
      meta: {
        item_netto: 0,
        item_requested: 0,
        item_diff: 0,
        item_limit: null
      },
      columns: [
        {
          field: 'id',
          label: 'ID',
          headerAlign: 'left'
        },
        {
          field: 'item_document_id',
          label: 'Dokumenten-ID',
          headerAlign: 'left'
        },
        {
          field: 'item_provision',
          label: 'Prämie',
          headerAlign: 'left',
          interpolate: true,
          representedAs: (row) => {
            if (row.item_provision) {
              return `<a href="/legacy/provision_edit/${row.item_provision}" target="_blank">${row.item_provision_name}</a>`
            }

            if (row.item_document_id) {
              return `Import-Referenz ${row.item_reference}`
            }

            return 'Manuelle Eingabe'
          }
        },
        {
          field: 'item_car',
          label: 'FIN',
          headerAlign: 'left',
          interpolate: true,
          representedAs: row => `<a href="/legacy/car_edit/${row.item_car}" target="_blank">${row.item_fin}</a>`
        },
        {
          field: 'item_date',
          label: 'Belegdatum',
          headerAlign: 'left',
          representedAs: row => DateTime.fromSQL(row.item_date).toFormat('dd.MM.yyyy')
        },
        {
          field: 'item_value',
          label: 'Zahlung Netto',
          headerAlign: 'left',
          class: 'text-right font-mono',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        {
          field: 'item_provision_value',
          label: 'Forderung Netto',
          headerAlign: 'left',
          class: 'text-right font-mono',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        {
          field: 'item_diff',
          label: 'Differenz Netto',
          headerAlign: 'left',
          class: 'text-right font-mono',
          component: PriceColumn,
          meta: {
            emptyValue: '0,00 EUR',
            symbol: 'EUR'
          }
        },
        {
          label: '',
          field: '',
          sortable: false,
          headerAlign: 'left',
          align: 'right',
          component: ActionButtonColumn,
          meta: {
            actions: [
              {
                icon: 'pencil-write',
                tooltip: 'Bearbeiten',
                class: 'text-gray-800 border-transparent shadow-none',
                onClick: (item) => {
                  this.$router.push(`/legacy/provision_paid_edit/${item.id}`)
                }
              },
              {
                icon: 'bin-1',
                tooltip: 'Löschen',
                class: 'text-gray-800 border-transparent shadow-none hover:bg-red-500',
                onClick: (item) => {
                  mia.provision.paid.remove.confirm(item.id, item.item_provision, item.item_fin, item.item_value)
                }
              }
            ]
          }
        }
      ],
      getPaidArchiveCancelToken: null
    }
  },

  computed: {
    firstDocumentId() {
      if (this.paidDocuments.length > 0) {
        return this.paidDocuments[0]
      }

      return ''
    },

    selectedDocumentId: {
      get() {
        if (this.$route.query.documentId) {
          return this.$route.query.documentId
        }

        return null
      },
      set(value) {
        if (value === null) {
          this.$router.replace({
            ...this.$route,
            query: {
              ...this.$route.query,
              documentId: undefined
            }
          })
          this.$nextTick(() => {
            this.setBreadcrumbs()
          })
          return
        }

        if (value === this.selectedDocumentId) {
          return
        }

        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            documentId: value
          }
        })
        this.$nextTick(() => {
          this.setBreadcrumbs()
        })
      }
    },

    selectedDateFrom: {
      get() {
        return this.$route.query.dateFrom || ''
      },
      set(value) {
        const newDateFrom = value || ''

        if (newDateFrom === this.selectedDateFrom) {
          return
        }

        if (newDateFrom !== '' && !DateTime.fromSQL(newDateFrom).isValid) {
          return
        }

        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            dateFrom: newDateFrom || undefined
          }
        })
        this.$nextTick(() => {
          this.setBreadcrumbs()
        })
      }
    },

    selectedDateTo: {
      get() {
        return this.$route.query.dateTo || ''
      },
      set(value) {
        const newDateTo = value || ''

        if (newDateTo === this.selectedDateTo) {
          return
        }

        if (newDateTo !== '' && !DateTime.fromSQL(newDateTo).isValid) {
          return
        }

        this.$router.replace({
          ...this.$route,
          query: {
            ...this.$route.query,
            dateTo: newDateTo || undefined
          }
        })
        this.$nextTick(() => {
          this.setBreadcrumbs()
        })
      }
    }
  },

  watch: {
    selectedDocumentId() {
      this.loadPaidArchive()
    },

    selectedDateFrom() {
      this.loadPaidArchive()
    },

    selectedDateTo() {
      this.loadPaidArchive()
    }
  },

  created() {
    this.loadPaidArchive()
    this.loadPaidDocuments()
  },

  mounted() {
    window.eventBus.$emit('mia-loading-ended')
    this.setBreadcrumbs()
  },

  methods: {
    setBreadcrumbs() {
      window.eventBus.$emit('mia-set-breadcrumb', [
        {
          link: {
            path: '/provisions'
          },
          text: 'Prämien'
        },
        {
          link: {
            path: '/provisions/paid-compare'
          },
          text: 'Zahlungsarchiv'
        }
      ])
    },

    async loadPaidArchive() {
      if (this.$wait.is('loading provision paid archive') && this.getPaidArchiveCancelToken) {
        this.getPaidArchiveCancelToken()
        console.log('canceled')
      }

      this.$wait.start('loading provision paid archive')
      const [error, response] = await getPaidArchive({
        item_document_id: this.selectedDocumentId === null ? undefined : this.selectedDocumentId,
        item_date_from: this.selectedDateFrom || undefined,
        item_date_to: this.selectedDateTo || undefined
      }, {
        cancelToken: new axios.CancelToken(((c) => {
          this.getPaidArchiveCancelToken = c
        }))
      })
      this.getPaidArchiveCancelToken = null
      this.$wait.end('loading provision paid archive')

      if (error) {
        if (axios.isCancel(error)) {
          return
        }

        window.eventBus.$emit('mia-error', {
          error: ['Zahlungsarchiv konnte nicht geladen werden']
        })
        return
      }

      this.paidArchive = response.data.data
      this.meta = {
        ...this.meta,
        ...response.data.meta
      }
    },

    async loadPaidDocuments() {
      this.$wait.start('loading provision paid documents')
      const [error, response] = await getPaidDocuments()
      this.$wait.end('loading provision paid documents')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Dokumente konnten nicht geladen werden']
        })
        return
      }

      this.paidDocuments = response.data
    },

    resetFilters() {
      this.$router.replace({
        ...this.$route,
        query: {
          ...this.$route.query,
          documentId: undefined,
          dateFrom: undefined,
          dateTo: undefined
        }
      })
      this.$nextTick(() => {
        this.setBreadcrumbs()
      })
    },

    onDateFromBlur(event) {
      this.selectedDateFrom = event.target.value
    },

    onDateToBlur(event) {
      this.selectedDateTo = event.target.value
    },

    onDateFromChange(event) {
      if (!/^[12]/.test(event.target.value)) {
        return
      }

      this.selectedDateFrom = event.target.value
    },

    onDateToChange(event) {
      if (!/^[12]/.test(event.target.value)) {
        return
      }

      this.selectedDateTo = event.target.value
    },

    async exportSelection() {
      if (this.$wait.is('exporting selected paid archive')) {
        return
      }

      this.$wait.start('exporting selected paid archive')
      const [error, response] = await exportPaidArchive({
        item_document_id: this.selectedDocumentId === '' ? '' : this.selectedDocumentId || undefined,
        item_date_from: this.selectedDateFrom || undefined,
        item_date_to: this.selectedDateTo || undefined
      })
      this.$wait.end('exporting selected paid archive')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Export fehlgeschlagen']
        })
        return
      }

      let fileName = `${DateTime.local().toFormat('yyyy-MM-dd')}-Carana-Praemien-Zahlungsarchiv.xlsx`

      if (response.headers['content-disposition']) {
        const matches = response.headers['content-disposition'].match(/filename="(.*?)"/)

        if (matches !== null) {
          fileName = matches[1].trim()
        }
      }

      download(response.data, fileName, response.data.type)

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich exportiert']
      })
    },

    async exportAll() {
      if (this.$wait.is('exporting complete paid archive')) {
        return
      }

      this.$wait.start('exporting complete paid archive')
      const [error, response] = await exportPaidArchive({
        export_all: 'Y'
      })
      this.$wait.end('exporting complete paid archive')

      if (error) {
        window.eventBus.$emit('mia-error', {
          error: ['Export fehlgeschlagen']
        })
        return
      }

      let fileName = `${DateTime.local().toFormat('yyyy-MM-dd')}-Carana-Praemien-Zahlungsarchiv.xlsx`

      if (response.headers['content-disposition']) {
        const matches = response.headers['content-disposition'].match(/filename="(.*?)"/)

        if (matches !== null) {
          fileName = matches[1].trim()
        }
      }

      download(response.data, fileName, response.data.type)

      window.eventBus.$emit('mia-info', {
        info: ['Daten erfolgreich exportiert']
      })
    },

    deleteSelection() {
      const documentId = this.selectedDocumentId === '' ? '' : this.selectedDocumentId

      mia.provision.paid.removeBatch.confirm(documentId, this.selectedDateFrom, this.selectedDateTo)
    }
  }
}
</script>

<template>
  <div class="w-full">
    <router-link
      to="/legacy/provision_paid_compare?force_legacy"
      class="text-orange-mia hover:text-orange-mia-dark hover:underline block mb-4"
    >
      Zum alten Zahlungsarchiv UI »
    </router-link>

    <div class="mt-2">
      <h1 class="my-0 py-0">
        Zahlungsarchiv
      </h1>

      <div class="mt-4 bg-gray-200 rounded p-4 shadow-xs">
        <div class="grid grid-cols-12 gap-6">
          <div class="col-span-2">
            <mia-ultimate-select-input
              v-model="selectedDocumentId"
              :options="paidDocuments"
              input-label="Dokumenten-ID"
            />
          </div>
          <div class="col-span-2">
            <mia-feature-input
              :value="selectedDateFrom"
              label="Datum von"
              type="date"
              @blur="onDateFromBlur"
              @change="onDateFromChange"
            />
          </div>
          <div class="col-span-2">
            <mia-feature-input
              :value="selectedDateTo"
              label="Datum bis"
              type="date"
              @blur="onDateToBlur"
              @change="onDateToChange"
            />
          </div>
          <div class="col-span-6 flex justify-end">
            <div>
              <button
                type="button"
                class="bg-green-700 hover:bg-green-800 rounded text-white inline-flex items-center px-3 py-2 focus:outline-none"
                @click="exportSelection"
              >
                <mia-spinner
                  v-if="$wait.is('exporting selected paid archive')"
                  class="w-5 h-5 mr-2"
                />
                <mia-streamline-icon
                  v-else
                  class="w-5 h-5 mr-2"
                  src="excel"
                />
                <span>Auswahl exportieren</span>
              </button>
              <button
                type="button"
                class="bg-green-700 hover:bg-green-800 rounded text-white inline-flex items-center px-3 py-2 ml-3 focus:outline-none"
                @click="exportAll"
              >
                <mia-spinner
                  v-if="$wait.is('exporting complete paid archive')"
                  class="w-5 h-5 mr-2"
                />
                <mia-streamline-icon
                  v-else
                  class="w-5 h-5 mr-2"
                  src="excel"
                />
                <span>Gesamtexport</span>
              </button>
              <button
                v-if="authUser.item_group === 'root'"
                type="button"
                class="bg-red-700 hover:bg-red-800 rounded text-white inline-flex items-center px-3 py-2 ml-12 focus:outline-none"
                @click="deleteSelection"
              >
                <mia-streamline-icon
                  class="w-5 h-5 mr-2 fill-current"
                  src="bin-1"
                />
                <span>Auswahl löschen</span>
              </button>
            </div>
          </div>
        </div>
        <div class="mt-2">
          <button
            type="button"
            class="text-orange-mia hover:underline focus:outline-none text-sm"
            @click="resetFilters"
          >
            Filter zurücksetzen
          </button>
        </div>
      </div>
      <div class="mt-4">
        <mia-wait for="loading provision paid archive">
          <template #waiting>
            <div class="flex items-center justify-center py-8">
              <mia-wave-loader />
            </div>
          </template>

          <template #default>
            <div
              v-if="meta.item_limit"
              class="mb-4"
            >
              <mia-notice
                permanent
                type="warning"
              >
                Das Ergebnis ist aufgrund der Datenmenge und der gesetzten Filter auf {{ meta.item_limit }} Einträge beschränkt.
              </mia-notice>
            </div>

            <datatable
              class="w-full table-auto datatable"
              :data="paidArchive"
              :columns="columns"
            >
              <template #footer>
                <tr>
                  <td
                    colspan="5"
                    class="font-bold"
                  >
                    Summe
                  </td>
                  <td class="text-right font-mono font-bold">
                    {{ formatAsCurrency(meta.item_netto, 'de-DE', { currencyDisplay: 'code' }) }}
                  </td>
                  <td class="text-right font-mono font-bold">
                    {{ formatAsCurrency(meta.item_requested, 'de-DE', { currencyDisplay: 'code' }) }}
                  </td>
                  <td class="text-right font-mono font-bold">
                    {{ formatAsCurrency(meta.item_diff, 'de-DE', { currencyDisplay: 'code' }) }}
                  </td>
                </tr>
              </template>
            </datatable>
          </template>
        </mia-wait>
      </div>
    </div>
  </div>
</template>
